import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { SnapTradeReact } from "snaptrade-react";
import { useAccount } from "../../context/AccountContext";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

export default function ConnectBrokeragePage() {
  const { selectedAccount } = useAccount();

  const [status, setStatus]   = useState(null); // { linked, brokerages, username }
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [loginLink, setLoginLink] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  // Re-fetches whenever the header account switcher changes selectedAccount —
  // status must reflect the currently selected account, not just "the"
  // session user's SnapTrade account (see /snaptrade/status backend note).
  const fetchStatus = useCallback(async () => {
    if (!selectedAccount || selectedAccount === "All Accounts") {
      setStatus({ linked: false, brokerages: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get("/snaptrade/status", { params: { username: selectedAccount } });
      setStatus(res.data);
    } catch {
      flash("Failed to load brokerage connection status.", false);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // The Connection Portal's customRedirect lands the user back here with
  // ?connected=1 as a fallback for cases the modal's postMessage misses
  // (e.g. the OAuth second-tab flow — see snaptrade-react's onExit note).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("connected") === "1") {
      window.history.replaceState({}, "", "/brokerage");
      fetchStatus();
      flash("Brokerage connected.");
    }
  }, [fetchStatus]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.post("/snaptrade/initiate", { username: selectedAccount });
      setLoginLink(res.data.redirect_url);
      setModalOpen(true);
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to start brokerage connection.", false);
    } finally {
      setConnecting(false);
    }
  };

  const handleSuccess = () => {
    setModalOpen(false);
    fetchStatus();
    flash("Brokerage connected successfully.");
  };

  const handleError = (err) => {
    setModalOpen(false);
    flash(err?.detail || "Brokerage connection failed. Please try again.", false);
  };

  const [disconnecting, setDisconnecting] = useState(null); // authorization_id currently disconnecting

  const disconnectBrokerage = async (authorizationId, name) => {
    if (!window.confirm(`Disconnect ${name}? This cannot be undone — ${appName} will stop trading and syncing this account.`)) return;
    setDisconnecting(authorizationId);
    try {
      await axios.post("/snaptrade/disconnect", { authorization_id: authorizationId, username: selectedAccount });
      flash(`${name} disconnected.`);
      fetchStatus();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to disconnect.", false);
    } finally {
      setDisconnecting(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 25 }}>
        <p style={{ color: "#888" }}>Loading…</p>
      </div>
    );
  }

  const isLinked = status?.linked && status.brokerages?.length > 0;

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Connect Your Brokerage</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Link your brokerage account so {appName} can trade and stay in sync on your behalf.
        </p>
      </div>

      {isLinked ? (
        <ConnectedView
          brokerages={status.brokerages}
          onAddAnother={startConnect}
          connecting={connecting}
          onDisconnect={disconnectBrokerage}
          disconnecting={disconnecting}
        />
      ) : (
        <EducationView onConnect={startConnect} connecting={connecting} />
      )}

      {loginLink && (
        <SnapTradeReact
          loginLink={loginLink}
          isOpen={modalOpen}
          close={() => setModalOpen(false)}
          onSuccess={handleSuccess}
          onError={handleError}
          onExit={() => setModalOpen(false)}
        />
      )}

      <GatewaySection username={selectedAccount} flash={flash} />
    </div>
  );
}

/* ── Advanced: self-hosted broker execution gateway (e.g. IBKR) ────────
   Independent of the SnapTrade flow above — a user may want this instead
   of (or alongside a different account's) SnapTrade connection. See
   docs/PARTNER_EXECUTION_GATEWAY.md / docs/PEG_API_REFERENCE.md. */

function GatewaySection({ username, flash }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null); // { configured, base_url, account_ref, partner_id }
  const [form, setForm] = useState({ base_url: "", account_ref: "", hmac_secret: "", partner_id: "" });

  const validAccount = username && username !== "All Accounts";

  const loadConfig = useCallback(async () => {
    if (!validAccount) return;
    setLoading(true);
    try {
      const res = await axios.get("/brokerage/gateway/config", { params: { username } });
      setConfig(res.data);
      if (res.data.configured) {
        setForm(f => ({ ...f, base_url: res.data.base_url, account_ref: res.data.account_ref, partner_id: res.data.partner_id || "" }));
      }
    } catch {
      // silent — this is an optional/advanced section, don't interrupt the main page with an error toast
    } finally {
      setLoading(false);
    }
  }, [username, validAccount]);

  useEffect(() => { if (expanded) loadConfig(); }, [expanded, loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post("/brokerage/gateway/config", { username, ...form });
      flash("Gateway configuration saved.");
      setForm(f => ({ ...f, hmac_secret: "" })); // never keep the secret in state after a successful save
      loadConfig();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to save gateway configuration.", false);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post("/brokerage/gateway/test-connection", { username });
      flash(`Connection OK — ${res.data.holdings_count} holding(s) returned.`);
    } catch (e) {
      flash(e?.response?.data?.error || "Connection test failed.", false);
    } finally {
      setTesting(false);
    }
  };

  const downloadDocs = () => { window.location.href = "/brokerage/gateway/api-docs"; };

  return (
    <div style={{ ...tile, marginTop: 20 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
      >
        <div>
          <div style={sectionTitle}>🛠️ Advanced: Connect Your Own Broker Gateway</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
            Run your own execution gateway (e.g. IBKR) instead of SnapTrade. Optional — most users won't need this.
          </div>
        </div>
        <span style={{ fontSize: 14, color: "#999" }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, borderTop: "1px solid #eee", paddingTop: 16 }}>
          <div style={{
            background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8,
            padding: "10px 14px", fontSize: 12.5, color: "#8a6d00", marginBottom: 16,
          }}>
            ⚠️ The reference IBKR adapter has not yet been verified against a live IBKR Gateway/TWS
            connection. Recommended for paper/testing use first — see the API reference for details.
          </div>

          {!validAccount ? (
            <p style={{ fontSize: 13, color: "#888" }}>Select a specific account (not "All Accounts") to configure a gateway.</p>
          ) : loading ? (
            <p style={{ fontSize: 13, color: "#888" }}>Loading…</p>
          ) : (
            <>
              {config?.configured && (
                <div style={{ fontSize: 12.5, color: "#28a745", marginBottom: 12 }}>
                  ✅ A gateway is configured for this account. Update any field below and save to change it.
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
                <FormField label="Gateway URL" placeholder="https://your-gateway.example.com"
                  value={form.base_url} onChange={v => setForm(f => ({ ...f, base_url: v }))} />
                <FormField label="Account Reference" placeholder="Your internal account identifier"
                  value={form.account_ref} onChange={v => setForm(f => ({ ...f, account_ref: v }))} />
                <FormField label={config?.configured ? "Signing Secret (leave blank to keep current)" : "Signing Secret"}
                  placeholder="HMAC signing secret" type="password"
                  value={form.hmac_secret} onChange={v => setForm(f => ({ ...f, hmac_secret: v }))} />
                <FormField label="Partner ID (optional)" placeholder=""
                  value={form.partner_id} onChange={v => setForm(f => ({ ...f, partner_id: v }))} />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <button type="button" onClick={handleSave} disabled={saving || !form.base_url || !form.account_ref} style={btnPrimaryLg}>
                  {saving ? "Saving…" : "Save Configuration"}
                </button>
                {config?.configured && (
                  <button type="button" onClick={handleTest} disabled={testing} style={btnSecondary}>
                    {testing ? "Testing…" : "Test Connection"}
                  </button>
                )}
                <button type="button" onClick={downloadDocs} style={btnSecondary}>
                  Download API Reference
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: "#555", fontWeight: 600 }}>
      {label}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
      />
    </label>
  );
}

/* ── Connected state ─────────────────────────────────────────── */

function ConnectedView({ brokerages, onAddAnother, connecting, onDisconnect, disconnecting }) {
  return (
    <div style={{ ...tile, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 18 }}>✅</span>
        <div style={sectionTitle}>Brokerage Connected</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {brokerages.map((b) => (
          <div key={b.id} style={brokerageRow}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#2c3e50" }}>{b.name}</div>
              <div style={{ fontSize: 12, color: b.trade_enabled ? "#28a745" : "#fd7e14" }}>
                {b.trade_enabled ? "Trading enabled" : "Read-only — reconnect to enable trading"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ ...badge, background: "#d4edda", color: "#155724" }}>Active</span>
              <button
                type="button"
                onClick={() => onDisconnect(b.id, b.name)}
                disabled={disconnecting === b.id}
                style={btnDanger}>
                {disconnecting === b.id ? "…" : "Disconnect"}
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAddAnother} disabled={connecting} style={btnSecondary}>
        {connecting ? "…" : "+ Add Another Brokerage"}
      </button>
    </div>
  );
}

/* ── Not-yet-connected / education state ─────────────────────── */

function EducationView({ onConnect, connecting }) {
  return (
    <>
      {/* Why connect */}
      <div style={{ ...grid3, marginBottom: 20 }}>
        <BenefitCard
          icon="⚡"
          title="Automated Execution"
          body={`Once connected, ${appName} can place and manage your options trades automatically, following your strategy's rules — no manual order entry required.`} />
        <BenefitCard
          icon="🔄"
          title="Real-Time Sync"
          body="Your positions, balances, and fills stay in sync automatically, so your dashboard always reflects what's actually in your brokerage account." />
        <BenefitCard
          icon="🏦"
          title="One Login, 35+ Brokerages"
          body="Powered by SnapTrade, a single secure connection covers Alpaca, Schwab, Fidelity, E*Trade, Robinhood, and dozens more — no separate setup per broker." />
      </div>

      {/* How it works */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>How It Works</div>
        <div style={stepsRow}>
          <Step n={1} text="Click Connect Brokerage below" />
          <StepArrow />
          <Step n={2} text="Choose your brokerage from the list" />
          <StepArrow />
          <Step n={3} text={`Log in securely on your broker's own site — never inside ${appName}`} />
          <StepArrow />
          <Step n={4} text="You're connected — trading and sync start automatically" />
        </div>
      </div>

      {/* Security */}
      <div style={{ ...tile, marginBottom: 20, borderLeft: "3px solid #17a2b8" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <div>
            <div style={{ ...sectionTitle, marginBottom: 6 }}>Your Credentials Stay Private</div>
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
              {appName} never sees or stores your brokerage login. The connection is handled entirely by{" "}
              <strong>SnapTrade</strong>, which is SOC&nbsp;2 Type&nbsp;II certified and encrypts all data
              with AWS KMS, both in transit and at rest. You can revoke access at any time from this page.
            </div>
          </div>
        </div>
      </div>

      <button type="button" onClick={onConnect} disabled={connecting} style={btnPrimaryLg}>
        {connecting ? "Starting…" : "Connect Brokerage"}
      </button>
    </>
  );
}

function BenefitCard({ icon, title, body }) {
  return (
    <div style={tile}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#2c3e50", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <div style={stepBox}>
      <div style={stepNum}>{n}</div>
      <div style={{ fontSize: 13, color: "#495057" }}>{text}</div>
    </div>
  );
}

function StepArrow() {
  return <div style={{ color: "#ccc", fontSize: 18, alignSelf: "center" }}>→</div>;
}

/* ── Styles ──────────────────────────────────────────────────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const tile         = { background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const grid3        = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 };
const sectionTitle = { fontSize: 14, fontWeight: 700, color: "#2c3e50" };
const brokerageRow = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8f9fa", borderRadius: 8 };
const badge         = { display: "inline-block", padding: "3px 9px", borderRadius: 10, fontSize: 11, fontWeight: 600 };
const btnPrimaryLg  = { padding: "12px 28px", borderRadius: 10, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 700 };
const btnSecondary  = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const btnDanger     = { padding: "5px 12px", borderRadius: 6, border: "1px solid #dc3545", background: "#fff", color: "#dc3545", cursor: "pointer", fontSize: 12 };
const stepsRow      = { display: "flex", alignItems: "stretch", gap: 12, marginTop: 12, flexWrap: "wrap" };
const stepBox       = { flex: "1 1 160px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 8, padding: "10px 8px" };
const stepNum       = { width: 28, height: 28, borderRadius: "50%", background: "#2c3e50", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 };
